import ky from "ky";
import { API_BASE_REPORTING } from "@/constants/api";
import { createUnauthorizedHook } from "@/apis/clients/kyHooks";
import type {
  PerformanceReportParams,
  PerformanceReportRequest,
  PerformanceReportResponse,
  ReportScope,
} from "@/types/reporting";

const PAGE_SIZE = 10_000;

// Safety-net above the worst-case server duration (30–60 s). The Go proxy
// enforces UPSTREAM_TIMEOUT_SECONDS on its own leg, but a stalled
// browser↔proxy connection has no other deadline — without this, a hung
// request permanently locks the request guard's in-flight state.
const CLIENT_TIMEOUT_MS = 90_000;

export const createReportingClient = () =>
  ky.create({
    baseUrl: API_BASE_REPORTING, // → https://t1qa1.mediamath.com/reporting/v2
    credentials: "same-origin",
    timeout: CLIENT_TIMEOUT_MS,
    hooks: {
      afterResponse: [createUnauthorizedHook()],
    },
  });

// Fetch all pages of a paginated report endpoint, combining results into one
// response. Generic so it works for any endpoint whose response shares the
// standard meta + results envelope (performance, arrival-standard, etc.).
// as T is safe: the spread preserves all T-specific fields; only results and
// items_count are overwritten by this function.
export const fetchAllPages = async <
  T extends {
    results: unknown[];
    meta: { items_count: number; total_pages: number };
  },
>(
  endpoint: string,
  request: PerformanceReportRequest
): Promise<T> => {
  const client = createReportingClient();
  const firstPage = await client.post(endpoint, { json: request }).json<T>();

  if (firstPage.meta.total_pages <= 1) {
    return firstPage;
  }

  const allResults: unknown[] = [...firstPage.results];

  const remainingPages = await Promise.all(
    Array.from({ length: firstPage.meta.total_pages - 1 }, (_, i) =>
      client
        .post(endpoint, {
          json: {
            ...request,
            pagination: { page_index: i + 1, page_size: PAGE_SIZE },
          },
        })
        .json<T>()
    )
  );
  remainingPages.forEach((page) => allResults.push(...page.results));

  return {
    ...firstPage,
    results: allResults,
    meta: { ...firstPage.meta, items_count: allResults.length },
  } as T;
};

// The API takes dimensions and metrics as one combined, deduped `fields`
// list rather than the two separate arrays the rest of this app's callers
// think in terms of.
//
// agg_timestamp is excluded even when passed as a dimension: it isn't a
// queryable field, it's the bucket a time-based rollup produces on its own —
// requesting it fails with "field does not exist in this dataset:
// agg_timestamp" (confirmed against the live API). rollup: "by_day" alone is
// what puts it on every result row, matching the API doc's own example
// (fields: [campaign_id, impressions], yet every result includes
// agg_timestamp).
//
// The scope filters name ids in fields the request doesn't select, and a
// filtered-but-unselected dimension is aggregated over server-side: the
// filtered ids come back as one summed row per day, not one row per id per
// day. Confirmed against the live API for an array of campaign_ids;
// organization_id and agency_status filters are assumed to behave the same
// way. The OpenAPI spec doesn't document this itself; only its filter schema
// documents that `value` may be an array at all ("Equals or contains if value
// is an array").
export const buildPerformanceRequest = (
  params: PerformanceReportParams
): PerformanceReportRequest => ({
  fields: [...new Set([...params.dimensions, ...params.metrics])].filter(
    (field) => field !== "agg_timestamp"
  ),
  filters: params.scopeFilters,
  rollup: "by_day",
  start_time: `${params.startDate}T00:00:00Z`,
  // Inclusivity of end_time isn't documented by the spec; treating it as the
  // last instant of the end date is an assumption to confirm against the
  // live API.
  end_time: `${params.endDate}T23:59:59Z`,
  pagination: { page_index: 0, page_size: PAGE_SIZE },
});

export const getPerformanceReport = (
  params: PerformanceReportParams
): Promise<PerformanceReportResponse> =>
  createReportingClient()
    .post("performance", { json: buildPerformanceRequest(params) })
    .json<PerformanceReportResponse>();

// Shared request builder for all rollup: "all" calls — aggregates the entire
// date window into one summed row (or one per dimension combination), no time series.
export const buildAllRollupRequest = (
  fields: readonly string[],
  { endDate, scopeFilters, startDate }: ReportScope
): PerformanceReportRequest => ({
  fields: [...fields],
  filters: scopeFilters,
  rollup: "all",
  start_time: `${startDate}T00:00:00Z`,
  end_time: `${endDate}T23:59:59Z`,
  pagination: { page_index: 0, page_size: PAGE_SIZE },
});

// Performance table uses rollup: "all" to aggregate the full date window into
// one summed row per dimension combination (not a row per day like the chart).
// Fields are fixed — the table always shows the same hierarchy + metric set.
export const TABLE_FIELDS = [
  "advertiser_name",
  "campaign_name",
  "strategy_group_name",
  "impressions",
  "clicks",
  "ctr",
  "video_complete",
  "video_complete_rate",
  "total_conversions",
  "total_spend_cpa",
  "total_spend_cpm",
] as const;

const CHANNEL_BREAKDOWN_FIELDS = [
  "strategy_channel",
  "impressions",
  "total_spend",
] as const;

export const getChannelBreakdownReport = (
  params: ReportScope
): Promise<PerformanceReportResponse> =>
  createReportingClient()
    .post("performance", {
      json: buildAllRollupRequest(CHANNEL_BREAKDOWN_FIELDS, params),
    })
    .json<PerformanceReportResponse>();

const CREATIVE_PERFORMANCE_FIELDS = [
  "clicks",
  "creative_id",
  "creative_name",
  "creative_size",
  "ctr",
  "impressions",
] as const;

export const getCreativePerformanceReport = (
  params: ReportScope
): Promise<PerformanceReportResponse> =>
  fetchAllPages(
    "performance",
    buildAllRollupRequest(CREATIVE_PERFORMANCE_FIELDS, params)
  );

const AUDIENCE_SEGMENT_FIELDS = [
  "audience_parent_vendor_name",
  "audience_segment_name",
  "audience_vendor_name",
  "clicks",
  "ctr",
  "impressions",
  "video_complete_rate",
] as const;

export const getAudienceSegmentReport = (
  params: ReportScope
): Promise<PerformanceReportResponse> =>
  fetchAllPages(
    "performance-audience-segments",
    buildAllRollupRequest(AUDIENCE_SEGMENT_FIELDS, params)
  );

export const getPerformanceTableReport = (
  params: ReportScope
): Promise<PerformanceReportResponse> =>
  fetchAllPages("performance", buildAllRollupRequest(TABLE_FIELDS, params));

// Device and geo have their own app-* endpoints (NP-1404), which are much
// faster than performance-audience-segments. They are internal beta: Reporting
// must sign off before production use and may change them without notice.
// performance-audience-segments is very expensive and is meant only for cards
// that need audience segments. If an app-* endpoint ever can't serve a field,
// fall back to all-dimensions-and-metrics, never to
// performance-audience-segments.
const DEVICE_BROWSER_FIELDS = [
  "browser",
  "clicks",
  "ctr",
  "impressions",
  "openrtb_device_type",
  "os_type",
] as const;

export const getDeviceBrowserReport = (
  params: ReportScope
): Promise<PerformanceReportResponse> =>
  fetchAllPages(
    "app-performance-device",
    buildAllRollupRequest(DEVICE_BROWSER_FIELDS, params)
  );

const GEO_FIELDS = [
  "city_name",
  "clicks",
  "ctr",
  "impressions",
  "region_name",
  "video_complete_rate",
] as const;

export const getGeoPerformanceReport = (
  params: ReportScope
): Promise<PerformanceReportResponse> =>
  fetchAllPages(
    "app-performance-geo",
    buildAllRollupRequest(GEO_FIELDS, params)
  );

// rollup: "all" aggregates all five quartile stages into a single row across
// the selected date window — correct for a funnel view which represents one
// period, not a time series.
const VIDEO_FUNNEL_FIELDS = [
  "video_complete",
  "video_complete_rate",
  "video_first_quartile",
  "video_midpoint",
  "video_start",
  "video_third_quartile",
] as const;

export const getVideoFunnelReport = (
  params: ReportScope
): Promise<PerformanceReportResponse> =>
  createReportingClient()
    .post("performance", {
      json: buildAllRollupRequest(VIDEO_FUNNEL_FIELDS, params),
    })
    .json<PerformanceReportResponse>();

// rollup: "all" sums rate fields (viewability_rate, viewability_rate_100_percent)
// across all rows, producing values like 80× that are meaningless as percentages.
// We fetch by_day rows with impressions so the widget can compute an
// impression-weighted average — matching how kpiDerivation derives CTR/VCR.
const VIEWABILITY_FIELDS = [
  "impressions",
  "viewability_rate",
  "viewability_rate_100_percent",
] as const;

export const getViewabilityReport = (
  params: ReportScope
): Promise<PerformanceReportResponse> =>
  fetchAllPages(
    "performance",
    buildPerformanceRequest({
      ...params,
      dimensions: [],
      metrics: [...VIEWABILITY_FIELDS],
    })
  );
